import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories, MAIN_SITE_URL } from '@/mocks/categories';

const languages = [
  { code: 'en', label: 'English' },
  { code: 'pl', label: 'Polski' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setLangOpen(false);
    setMobileOpen(false);
  }, [location]);

  const changeLang = (code: string) => {
    i18n.changeLanguage(code);
    document.documentElement.lang = code;
    setLangOpen(false);
  };

  const currentLang = languages.find((l) => l.code === i18n.language)?.label ?? 'English';

  const navLinks = [
    { label: t('nav.home'), to: '/' },
    { label: t('nav.spreadsheet'), to: '/#what' },
    { label: t('nav.categories'), to: '/#categories' },
    { label: t('nav.guides'), to: '/usfans-spreadsheet-guide' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200">
      <nav className="mx-auto flex items-center justify-between px-4 md:px-6 h-16 md:h-20">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
          <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary-500 text-background-50">
            <i className="ri-file-list-3-line text-base" />
          </span>
          <span className="font-heading font-bold text-foreground-950 text-base md:text-lg">
            {t('brand')}
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="px-3 py-2 text-sm font-medium text-foreground-900 hover:text-primary-700 hover:bg-primary-100/70 rounded-md transition-colors whitespace-nowrap"
            >
              {link.label}
            </Link>
          ))}
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="ml-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <span className="flex items-center justify-center w-4 h-4">
              <i className="ri-shopping-bag-3-line text-sm" />
            </span>
            {t('nav.shop')}
          </a>
        </div>

        <div className="flex items-center gap-2">
          {/* Language switcher */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground-900 hover:bg-background-100 rounded-md transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="flex items-center justify-center w-5 h-5">
                <i className="ri-global-line text-base" />
              </span>
              <span>{currentLang}</span>
              <i className={`ri-arrow-down-s-line text-sm transition-transform ${langOpen ? 'rotate-180' : ''}`} />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-44 bg-background-50 border border-background-200 rounded-lg p-1 z-50">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => changeLang(lang.code)}
                    className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                      i18n.language === lang.code
                        ? 'bg-primary-100 text-primary-700 font-semibold'
                        : 'text-foreground-900 hover:bg-background-100'
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-md text-foreground-950 hover:bg-background-100 cursor-pointer"
            aria-label="Menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200 px-4 py-4 max-h-[75vh] overflow-y-auto">
          <div className="flex flex-col gap-1 mb-4">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="px-3 py-2.5 text-sm font-medium text-foreground-900 hover:bg-primary-100/70 rounded-md whitespace-nowrap"
              >
                {link.label}
              </Link>
            ))}
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="mt-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <span className="flex items-center justify-center w-4 h-4">
                <i className="ri-shopping-bag-3-line text-sm" />
              </span>
              {t('nav.shop')}
            </a>
          </div>

          <p className="px-1 pb-2 text-xs font-semibold uppercase tracking-wide text-foreground-500">
            {t('nav.categories')}
          </p>
          <div className="grid grid-cols-2 gap-1.5">
            {categories.map((cat) => (
              <a
                key={cat.id}
                href={cat.link}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-between gap-1 px-3 py-2 text-sm text-foreground-800 bg-background-100/70 rounded-md hover:bg-primary-100/70 transition-colors whitespace-nowrap"
              >
                <span className="truncate">{t(`cat.${cat.id}`)}</span>
                <span className="flex items-center justify-center w-4 h-4 text-foreground-400 shrink-0">
                  <i className="ri-external-link-line text-xs" />
                </span>
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}